// Local replacement for the abandoned `react-simple-dropdown` package, which was
// frozen at React 16 (used string refs + findDOMNode) and blocked the React 18
// upgrade. This reproduces the exact API the app relies on:
//   import Dropdown, { DropdownTrigger, DropdownContent } from '.../SimpleDropdown'
//   <Dropdown ref={r} onShow onHide disabled active>
//     <DropdownTrigger>...</DropdownTrigger>
//     <DropdownContent>...</DropdownContent>
//   </Dropdown>
// - ref exposes imperative hide()/show() (EditButtonsSections calls ref.current.hide()).
// - class names match the original CSS: dropdown, dropdown--active,
//   dropdown--disabled, dropdown-trigger, dropdown__content.
// - closes on outside click / touch, like the original.

import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useRef,
  useEffect,
  useCallback,
  Children,
  isValidElement,
  cloneElement,
} from 'react'
import classNames from 'classnames'

export const DropdownTrigger = ({ children, className = '', onClick, ...rest }) => (
  <a className={classNames('dropdown-trigger', className)} onClick={onClick} {...rest}>
    {children}
  </a>
)
DropdownTrigger.displayName = 'DropdownTrigger'

export const DropdownContent = ({ children, className = '', ...rest }) => (
  <div className={classNames('dropdown__content', className)} {...rest}>
    {children}
  </div>
)
DropdownContent.displayName = 'DropdownContent'

const Dropdown = forwardRef(function Dropdown(props, ref) {
  const {
    children,
    className = '',
    disabled = false,
    active: controlledActive,
    onShow,
    onHide,
    removeElement = false,
    // eslint-disable-next-line no-unused-vars
    ...rest
  } = props

  const [uncontrolledActive, setUncontrolledActive] = useState(false)
  const isControlled = typeof controlledActive === 'boolean'
  const active = isControlled ? controlledActive : uncontrolledActive

  const rootRef = useRef(null)

  const hide = useCallback(() => {
    setUncontrolledActive(false)
    if (onHide) onHide()
  }, [onHide])

  const show = useCallback(() => {
    setUncontrolledActive(true)
    if (onShow) onShow()
  }, [onShow])

  const toggle = useCallback(() => {
    if (active) hide()
    else show()
  }, [active, hide, show])

  useImperativeHandle(ref, () => ({ hide, show }), [hide, show])

  useEffect(() => {
    if (!active) return undefined
    const onWindowClick = (event) => {
      const el = rootRef.current
      if (el && event.target !== el && !el.contains(event.target)) {
        hide()
      }
    }
    window.addEventListener('click', onWindowClick)
    window.addEventListener('touchstart', onWindowClick)
    return () => {
      window.removeEventListener('click', onWindowClick)
      window.removeEventListener('touchstart', onWindowClick)
    }
  }, [active, hide])

  const dropdownClasses = classNames(
    {
      dropdown: true,
      'dropdown--active': active,
      'dropdown--disabled': disabled,
    },
    className
  )

  // Wire the trigger's onClick to toggle; hide content when inactive if removeElement.
  const boundChildren = Children.map(children, (child) => {
    if (!isValidElement(child)) return child

    if (child.type === DropdownTrigger) {
      const originalOnClick = child.props.onClick
      return cloneElement(child, {
        onClick: (event) => {
          if (disabled) return
          toggle()
          if (originalOnClick) originalOnClick(event)
        },
      })
    }

    if (child.type === DropdownContent && removeElement && !active) {
      return null
    }

    return child
  })

  return (
    <div ref={rootRef} className={dropdownClasses}>
      {boundChildren}
    </div>
  )
})

export default Dropdown
